/**
 * Layout builders for the template gallery. Every builder lays the design out with stacks
 * (auto layout), so a gallery template keeps working when it is resized or gets longer texts.
 */
import {
  createEmptyDoc,
  createGroupLayer,
  createImageLayer,
  createShapeLayer,
  createTextLayer,
  newLayerId,
} from '../factory';
import type { Fill, GroupLayer, Layer, TemplateDoc, TextLayer } from '../template';
import { GALLERY_SRC_PREFIX, type GalleryImageId, type IllustrationId } from './illustrations';
import {
  barLayout,
  beforeAfterLayout,
  editorialLayout,
  posterLayout,
  stackLayout,
  storyCardLayout,
} from './photo-layouts';
import { isPhotoId, PHOTOS } from './photos';

export interface Palette {
  background: Fill;
  title: string;
  text: string;
  accent: string;
  accentText: string;
  /** Font of the headline; body text uses `bodyFont`. */
  titleFont: string;
  bodyFont: string;
}

export interface Copy {
  kicker?: string;
  title: string;
  subtitle?: string;
  cta?: string;
  price?: string;
  badge?: string;
  author?: string;
  role?: string;
  details?: string[];
  brand?: string;
  /** Sports: the two teams and a final score ("2 : 1"). */
  home?: string;
  away?: string;
  score?: string;
}

export interface BuildInput {
  width: number;
  height: number;
  palette: Palette;
  copy: Copy;
  image?: GalleryImageId;
  /** Sports: background scene and the two crests. */
  images?: IllustrationId[];
  /** Photo layouts: Unsplash search queries (English) for the main / second photo. */
  photo?: string;
  photo2?: string;
}

const pad = (n: number) => ({ top: n, right: n, bottom: n, left: n });
const solid = (color: string): Fill => ({ type: 'solid', color });

/** Small helper that keeps ids/names unique and wires `parentId`. */
export class Builder {
  doc: TemplateDoc;
  /** Typographic unit: 1 at 1080 px on the shorter side. */
  u: number;
  constructor(public input: BuildInput) {
    this.doc = createEmptyDoc(input.width, input.height);
    this.doc.background = input.palette.background;
    this.u = Math.min(input.width, input.height) / 1080;
  }
  px(n: number) {
    return Math.max(1, Math.round(n * this.u));
  }
  add<T extends Layer>(layer: T): T {
    this.doc.layers.push(layer);
    return layer;
  }
  stack(name: string, parent: GroupLayer | null, props: Partial<GroupLayer>): GroupLayer {
    return this.add(
      createGroupLayer({
        id: newLayerId(),
        name,
        x: 0,
        y: 0,
        width: this.input.width,
        height: this.input.height,
        ...(parent ? { parentId: parent.id } : {}),
        ...props,
      }),
    );
  }
  text(name: string, parent: GroupLayer | null, props: Partial<TextLayer>): TextLayer {
    const p = this.input.palette;
    return this.add(
      createTextLayer({
        id: newLayerId(),
        name,
        fontFamily: p.bodyFont,
        fontWeight: 500,
        color: p.text,
        lineHeight: 1.25,
        width: this.px(600),
        height: this.px(80),
        ...(parent ? { parentId: parent.id } : {}),
        ...props,
      }),
    );
  }
  title(parent: GroupLayer, size: number, props: Partial<TextLayer> = {}) {
    const p = this.input.palette;
    return this.text('title', parent, {
      text: this.input.copy.title,
      fontFamily: p.titleFont,
      fontWeight: 800,
      fontSize: this.px(size),
      lineHeight: 1.05,
      color: p.title,
      autoFit: true,
      minFontSize: Math.max(8, this.px(size * 0.4)),
      maxLines: 3,
      description: 'Headline',
      ...props,
    });
  }
  button(name: string, parent: GroupLayer, text: string, size = 34) {
    const p = this.input.palette;
    return this.text(name, parent, {
      text,
      fontFamily: p.bodyFont,
      fontWeight: 700,
      fontSize: this.px(size),
      color: p.accentText,
      align: 'center',
      verticalAlign: 'middle',
      background: solid(p.accent),
      padding: { top: this.px(18), right: this.px(36), bottom: this.px(18), left: this.px(36) },
      borderRadius: this.px(60),
      // Wide enough for long one-word CTAs ("Зареєструватися") in narrow banners
      width: Math.min(this.input.width - this.px(128), this.px(size * 12)),
      height: this.px(size * 2.2),
      autoFit: true,
      minFontSize: Math.max(5, this.px(size * 0.35)),
    });
  }
  image(name: string, parent: GroupLayer | null, props: Partial<Layer> = {}) {
    const id = this.input.image;
    const photo = id && isPhotoId(id) ? PHOTOS[id] : undefined;
    return this.add(
      createImageLayer({
        id: newLayerId(),
        name,
        src: id ? `${GALLERY_SRC_PREFIX}${id}` : '',
        x: 0,
        y: 0,
        focusX: photo?.focusX ?? 0.5,
        focusY: photo?.focusY ?? 0.5,
        width: this.px(480),
        height: this.px(480),
        ...(parent ? { parentId: parent.id } : {}),
        ...(props as object),
      }),
    );
  }
}

/** Text column + picture: a row in landscape sizes, a column in square and portrait ones. */
export function splitLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p } = input;
  const root = b.stack('layout', null, {
    direction: 'auto',
    // Square sizes stack text over the picture: side by side the picture gets too narrow
    autoRowRatio: 1.25,
    alignItems: 'stretch',
    gap: b.px(48),
    padding: pad(b.px(64)),
  });
  const content = b.stack('content', root, {
    grow: true,
    direction: 'auto',
    autoRowRatio: 2.5,
    justify: 'space-between',
    alignItems: 'stretch',
    gap: b.px(28),
    width: b.px(460),
    height: b.px(900),
  });
  const head = b.stack('headline', content, {
    direction: 'vertical',
    alignItems: 'stretch',
    gap: b.px(18),
  });
  if (copy.kicker)
    b.text('kicker', head, {
      text: copy.kicker.toUpperCase(),
      fontWeight: 700,
      fontSize: b.px(26),
      letterSpacing: b.px(3),
      color: p.accent,
    });
  b.title(head, 84);
  if (copy.subtitle)
    b.text('subtitle', head, {
      text: copy.subtitle,
      fontSize: b.px(34),
      lineHeight: 1.3,
      maxLines: 3,
    });
  if (copy.badge || copy.price || copy.cta) {
    const offer = b.stack('offer', content, {
      direction: 'horizontal',
      alignItems: 'center',
      justify: copy.cta && !copy.price ? 'start' : 'space-between',
      gap: b.px(24),
    });
    if (copy.badge)
      b.text('badge', offer, {
        text: copy.badge,
        fontFamily: 'Unbounded',
        fontWeight: 800,
        fontSize: b.px(44),
        color: p.accentText,
        align: 'center',
        background: solid(p.accent),
        padding: { top: b.px(12), right: b.px(28), bottom: b.px(12), left: b.px(28) },
        borderRadius: b.px(60),
        width: b.px(220),
        autoFit: true,
        minFontSize: 8,
      });
    if (copy.price)
      b.text('price', offer, {
        text: copy.price,
        fontFamily: 'Golos Text',
        fontWeight: 700,
        fontSize: b.px(64),
        color: p.title,
        align: 'right',
        width: b.px(240),
        autoFit: true,
        minFontSize: 8,
      });
    if (copy.cta && !copy.price) b.button('cta', offer, copy.cta);
  }
  b.image('photo', root, { grow: true, borderRadius: b.px(32) } as Partial<Layer>);
  return b.doc;
}

/** Full-bleed picture with a darkening overlay and a centred message. */
export function heroLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p } = input;
  if (input.image) {
    b.image('photo', null, { width: input.width, height: input.height });
    b.add(
      createShapeLayer({
        id: newLayerId(),
        name: 'overlay',
        x: 0,
        y: 0,
        width: input.width,
        height: input.height,
        fill: {
          type: 'linear',
          angle: 180,
          stops: [
            { offset: 0, color: '#0F172A33' },
            { offset: 1, color: '#0F172AE6' },
          ],
        },
      }),
    );
  }
  const root = b.stack('layout', null, {
    direction: 'vertical',
    alignItems: 'center',
    justify: input.height > input.width ? 'end' : 'center',
    gap: b.px(28),
    padding: pad(b.px(80)),
  });
  if (copy.kicker)
    b.text('kicker', root, {
      text: copy.kicker.toUpperCase(),
      fontWeight: 700,
      fontSize: b.px(28),
      letterSpacing: b.px(4),
      color: p.accent,
      align: 'center',
      width: b.px(900),
    });
  b.title(root, 104, { align: 'center', width: b.px(900) });
  if (copy.subtitle)
    b.text('subtitle', root, {
      text: copy.subtitle,
      fontSize: b.px(36),
      lineHeight: 1.35,
      align: 'center',
      width: b.px(800),
      maxLines: 3,
    });
  if (copy.cta) b.button('cta', root, copy.cta, 36);
  return b.doc;
}

/** Picture on top, text card below (stories, product cards). */
export function cardLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p } = input;
  const root = b.stack('layout', null, {
    direction: 'vertical',
    alignItems: 'stretch',
    gap: b.px(44),
    padding: pad(b.px(64)),
  });
  b.image('photo', root, { grow: true, borderRadius: b.px(40) } as Partial<Layer>);
  const text = b.stack('content', root, {
    direction: 'vertical',
    alignItems: 'stretch',
    gap: b.px(20),
  });
  if (copy.kicker)
    b.text('kicker', text, {
      text: copy.kicker.toUpperCase(),
      fontWeight: 700,
      fontSize: b.px(28),
      letterSpacing: b.px(3),
      color: p.accent,
    });
  b.title(text, 92);
  if (copy.subtitle)
    b.text('subtitle', text, {
      text: copy.subtitle,
      fontSize: b.px(36),
      lineHeight: 1.3,
      maxLines: 3,
    });
  if (copy.price || copy.cta) {
    const row = b.stack('offer', text, {
      direction: 'horizontal',
      alignItems: 'center',
      justify: 'space-between',
      gap: b.px(24),
    });
    if (copy.price)
      b.text('price', row, {
        text: copy.price,
        fontFamily: 'Golos Text',
        fontWeight: 700,
        fontSize: b.px(64),
        color: p.title,
        width: b.px(300),
        autoFit: true,
        minFontSize: 8,
      });
    if (copy.cta) b.button('cta', row, copy.cta);
  }
  return b.doc;
}

/** Big quote with the author (testimonials, quotes of the day). */
export function quoteLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p } = input;
  const root = b.stack('layout', null, {
    direction: 'vertical',
    alignItems: 'stretch',
    justify: 'center',
    gap: b.px(36),
    padding: pad(b.px(96)),
  });
  b.text('quote_mark', root, {
    text: '“',
    fontFamily: 'Playfair Display',
    fontWeight: 700,
    fontSize: b.px(220),
    lineHeight: 0.8,
    color: p.accent,
  });
  b.title(root, 64, { fontWeight: 600, lineHeight: 1.2, maxLines: 6, minFontSize: b.px(26) });
  const author = b.stack('byline', root, {
    direction: 'horizontal',
    alignItems: 'center',
    gap: b.px(24),
  });
  if (input.image)
    b.image('avatar', author, {
      width: b.px(110),
      height: b.px(110),
      borderRadius: b.px(55),
    } as Partial<Layer>);
  else
    b.add(
      createShapeLayer({
        id: newLayerId(),
        name: 'avatar',
        parentId: author.id,
        shape: 'ellipse',
        width: b.px(110),
        height: b.px(110),
        fill: solid(p.accent),
      }),
    );
  const who = b.stack('who', author, {
    grow: true,
    direction: 'vertical',
    gap: b.px(4),
    alignItems: 'stretch',
  });
  b.text('author', who, {
    text: copy.author ?? '',
    fontWeight: 700,
    fontSize: b.px(34),
    color: p.title,
  });
  b.text('role', who, { text: copy.role ?? '', fontSize: b.px(28) });
  return b.doc;
}

/** Event / webinar: date badge, title, details and a call to action. */
export function eventLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p } = input;
  if (input.image) {
    b.image('photo', null, {
      x: Math.round(input.width * 0.52),
      y: 0,
      width: Math.round(input.width * 0.48),
      height: input.height,
      constraints: { horizontal: 'right', vertical: 'stretch' },
    } as Partial<Layer>);
  }
  const root = b.stack('layout', null, {
    direction: 'vertical',
    alignItems: 'start',
    justify: 'space-between',
    gap: b.px(32),
    padding: pad(b.px(72)),
    width: input.image ? Math.round(input.width * 0.52) : input.width,
    constraints: input.image ? { horizontal: 'left', vertical: 'stretch' } : undefined,
  });
  if (copy.kicker)
    b.text('date', root, {
      text: copy.kicker,
      fontFamily: 'Unbounded',
      fontWeight: 700,
      fontSize: b.px(34),
      color: p.accentText,
      background: solid(p.accent),
      padding: { top: b.px(14), right: b.px(28), bottom: b.px(14), left: b.px(28) },
      borderRadius: b.px(16),
      width: b.px(640),
      autoFit: true,
      minFontSize: 8,
    });
  const body = b.stack('content', root, {
    direction: 'vertical',
    alignItems: 'stretch',
    gap: b.px(22),
  });
  b.title(body, 80, { width: b.px(900) });
  for (const [i, line] of (copy.details ?? []).entries())
    b.text(`detail_${i + 1}`, body, { text: `•  ${line}`, fontSize: b.px(32), width: b.px(900) });
  if (copy.cta) b.button('cta', root, copy.cta);
  return b.doc;
}

/** Huge number (discount, countdown) with a short message. */
export function numberLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p } = input;
  b.add(
    createShapeLayer({
      id: newLayerId(),
      name: 'circle',
      shape: 'ellipse',
      x: Math.round(input.width * 0.55),
      y: -Math.round(input.height * 0.12),
      width: b.px(720),
      height: b.px(720),
      fill: solid(p.accent),
      opacity: 0.18,
    }),
  );
  const root = b.stack('layout', null, {
    direction: 'vertical',
    alignItems: 'start',
    justify: 'center',
    gap: b.px(24),
    padding: pad(b.px(88)),
  });
  if (copy.kicker)
    b.text('kicker', root, {
      text: copy.kicker.toUpperCase(),
      fontWeight: 800,
      fontSize: b.px(34),
      letterSpacing: b.px(6),
      color: p.accent,
    });
  b.text('badge', root, {
    text: copy.badge ?? '-50%',
    fontFamily: p.titleFont,
    fontWeight: 900,
    fontSize: b.px(300),
    lineHeight: 0.95,
    color: p.title,
    width: b.px(900),
    autoFit: true,
    minFontSize: 12,
  });
  b.title(root, 72, { width: b.px(900) });
  if (copy.subtitle)
    b.text('subtitle', root, {
      text: copy.subtitle,
      fontSize: b.px(34),
      width: b.px(820),
      maxLines: 3,
    });
  if (copy.cta) b.button('cta', root, copy.cta);
  return b.doc;
}

/** One-line web banners (728×90, 970×250, 320×50): brand, message, button. */
export function stripLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p } = input;
  const h = input.height;
  // Sizes follow the banner height, but never so big that the message gets no room
  const k = Math.min(h / 90, input.width / 728);
  const px = (n: number) => Math.max(1, Math.round(n * k));
  const root = b.stack('layout', null, {
    direction: 'horizontal',
    alignItems: 'center',
    gap: px(16),
    padding: { top: px(10), right: px(16), bottom: px(10), left: px(16) },
  });
  if (input.image)
    b.image('photo', root, {
      width: px(70),
      height: px(70),
      borderRadius: px(10),
    } as Partial<Layer>);
  if (copy.brand)
    b.text('brand', root, {
      text: copy.brand,
      fontFamily: p.titleFont,
      fontWeight: 800,
      fontSize: px(20),
      color: p.accent,
      width: px(130),
      autoFit: true,
      minFontSize: 6,
    });
  const msg = b.stack('message', root, {
    grow: true,
    direction: 'vertical',
    alignItems: 'stretch',
    gap: px(2),
  });
  b.title(msg, 0, {
    fontSize: px(24),
    minFontSize: 6,
    maxLines: 2,
    width: px(300),
  });
  if (copy.subtitle)
    b.text('subtitle', msg, {
      text: copy.subtitle,
      fontSize: px(14),
      maxLines: 1,
      autoFit: true,
      minFontSize: 6,
    });
  if (copy.cta)
    b.text('cta', root, {
      text: copy.cta,
      fontWeight: 700,
      fontSize: px(18),
      color: p.accentText,
      align: 'center',
      background: solid(p.accent),
      padding: { top: px(10), right: px(18), bottom: px(10), left: px(18) },
      borderRadius: px(30),
      width: px(150),
      autoFit: true,
      minFontSize: 6,
    });
  return b.doc;
}

/**
 * Match day / full time: a stadium scene, two crests side by side with "VS" or the score,
 * league and label on top, date and channel below. Absolute positions scaled to the canvas,
 * so the same design fits a post, a story and a wide screen.
 */
function matchBase(input: BuildInput, result: boolean): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p, width: w, height: h } = input;
  // AI layouts pass one picture and team names in `details`; fall back to the stadium set
  const [scene, crestA, crestB] = input.images?.length
    ? input.images
    : [input.image ?? 'stadium', 'crest-red' as const, 'crest-blue' as const];
  const home = copy.home ?? copy.details?.[0] ?? '';
  const away = copy.away ?? copy.details?.[1] ?? '';
  const src = (id?: GalleryImageId) => (id ? `${GALLERY_SRC_PREFIX}${id}` : '');
  const portrait = h > w * 1.2;
  const landscape = w > h * 1.2;
  const m = b.px(70);
  b.add(
    createImageLayer({
      id: newLayerId(),
      name: 'background',
      x: 0,
      y: 0,
      width: w,
      height: h,
      src: src(scene),
    }),
  );
  b.add(
    createShapeLayer({
      id: newLayerId(),
      name: 'shade',
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: {
        type: 'linear',
        angle: 180,
        stops: [
          { offset: 0, color: '#02061799' },
          { offset: 0.45, color: '#02061733' },
          { offset: 1, color: '#020617F0' },
        ],
      },
    }),
  );
  const top = (name: string, text: string, x: number, align: 'left' | 'right', accent: boolean) =>
    b.text(name, null, {
      text,
      x,
      y: m,
      width: Math.round(w / 2 - m * 1.2),
      height: b.px(70),
      fontFamily: p.titleFont,
      fontWeight: 800,
      fontSize: b.px(30),
      letterSpacing: b.px(3),
      textTransform: 'uppercase',
      align,
      verticalAlign: 'middle',
      color: accent ? p.accentText : '#FFFFFF',
      autoFit: true,
      maxLines: 1,
      minFontSize: b.px(12),
      highlight: accent
        ? { color: p.accent, paddingX: b.px(16), paddingY: b.px(6), radius: b.px(8) }
        : null,
    });
  if (copy.kicker) top('league', copy.kicker, m, 'left', false);
  if (copy.badge) top('label', copy.badge, Math.round(w / 2 + m * 0.2), 'right', true);

  const size = Math.round(Math.min(w * (portrait ? 0.36 : 0.27), h * (landscape ? 0.38 : 0.3)));
  const cy = Math.round(h * (portrait ? 0.36 : landscape ? 0.43 : 0.4));
  // Crests (and names under them) stay inside the canvas on narrow formats
  const gap = Math.round(Math.min(size * (result ? 1.15 : 0.95), w / 2 - size / 2 - m * 0.6));
  const nameW = Math.round(Math.min(size * 1.6, w / 2 - m * 0.8, (gap - size * 0.1) * 2));
  const crest = (name: string, id: IllustrationId | undefined, cx: number) =>
    b.add(
      createImageLayer({
        id: newLayerId(),
        name,
        x: Math.round(cx - size / 2),
        y: Math.round(cy - size / 2),
        width: size,
        height: size,
        fit: 'contain',
        src: src(id),
      }),
    );
  crest('home_logo', crestA, w / 2 - gap);
  crest('away_logo', crestB, w / 2 + gap);
  const mid = result ? (copy.score ?? copy.title ?? '0 : 0') : 'VS';
  b.text(result ? 'score' : 'vs', null, {
    text: mid,
    x: Math.round(w / 2 - size * 0.75),
    y: Math.round(cy - size * 0.35),
    width: Math.round(size * 1.5),
    height: Math.round(size * 0.7),
    fontFamily: p.titleFont,
    fontWeight: 900,
    fontSize: Math.round(size * (result ? 0.42 : 0.3)),
    lineHeight: 1,
    align: 'center',
    verticalAlign: 'middle',
    color: result ? '#FFFFFF' : p.accent,
    autoFit: true,
    maxLines: 1,
    minFontSize: 10,
    shadow: { x: 0, y: b.px(6), blur: b.px(24), color: '#000000AA' },
  });
  const nameY = Math.round(cy + size / 2 + b.px(18));
  const team = (name: string, text: string | undefined, cx: number) =>
    b.text(name, null, {
      text: text ?? '',
      x: Math.round(cx - nameW / 2),
      y: nameY,
      width: nameW,
      height: b.px(portrait ? 120 : 96),
      fontFamily: p.titleFont,
      fontWeight: 800,
      fontSize: b.px(portrait ? 46 : 40),
      lineHeight: 1.1,
      align: 'center',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      autoFit: true,
      maxLines: 2,
      minFontSize: b.px(14),
    });
  team('home', home, w / 2 - gap);
  team('away', away, w / 2 + gap);

  const infoY = nameY + b.px(portrait ? 190 : landscape ? 130 : 140);
  if (copy.subtitle)
    b.text(result ? 'scorers' : 'date', null, {
      text: copy.subtitle,
      x: m,
      y: infoY,
      width: w - m * 2,
      height: b.px(result ? 110 : 90),
      fontFamily: result ? p.bodyFont : p.titleFont,
      fontWeight: result ? 500 : 800,
      fontSize: b.px(result ? 32 : 54),
      lineHeight: 1.25,
      align: 'center',
      color: result ? '#E2E8F0' : '#FFFFFF',
      autoFit: true,
      maxLines: 2,
      minFontSize: b.px(14),
    });
  if (copy.brand)
    b.text('channel', null, {
      text: copy.brand,
      x: m,
      y: h - m - b.px(64),
      width: Math.round(w / 2 - m),
      height: b.px(64),
      fontFamily: p.titleFont,
      fontWeight: 800,
      fontSize: b.px(30),
      letterSpacing: b.px(2),
      verticalAlign: 'middle',
      color: '#FFFFFF',
      autoFit: true,
      maxLines: 1,
      minFontSize: b.px(12),
    });
  if (copy.cta) {
    const btn = b.button('cta', null as unknown as GroupLayer, copy.cta, 30);
    delete btn.parentId;
    btn.x = Math.round(w - m - btn.width * (portrait ? 1 : 0.62));
    btn.width = Math.round(btn.width * (portrait ? 1 : 0.62));
    if (portrait) {
      btn.x = m;
      btn.width = w - m * 2;
      btn.y = h - m - b.px(64) - b.px(40) - btn.height;
    } else btn.y = h - m - btn.height;
  }
  return b.doc;
}

export const matchLayout = (input: BuildInput) => matchBase(input, false);
export const resultLayout = (input: BuildInput) => matchBase(input, true);

export const LAYOUTS = {
  split: splitLayout,
  hero: heroLayout,
  card: cardLayout,
  quote: quoteLayout,
  event: eventLayout,
  number: numberLayout,
  strip: stripLayout,
  match: matchLayout,
  result: resultLayout,
  poster: posterLayout,
  bar: barLayout,
  editorial: editorialLayout,
  storyCard: storyCardLayout,
  stack: stackLayout,
  beforeAfter: beforeAfterLayout,
} as const;
export type LayoutId = keyof typeof LAYOUTS;
